// Moving things in the rooms (world.json `fx`), drawn in art pixels over the floors: clock hands at the real time,
// fountains, flags, trees, dust in window light, steam, stirring drapes, pigeons, glints, lamps at night, and cloud
// shadows over the map. Every effect is a pure function of the clock; `animate` false (reduced motion) draws the rest
// state (the clocks still tell the time).
import type { FxEntry, WorldMeta } from './assets';
import { hashStr } from './life';

const P = {
  ol: '#160f1a', m0: '#857d73', m1: '#a89f92', m2: '#c8c0b2', m3: '#e2dcd0', m4: '#f5f2ea',
  v1: '#65111d', v2: '#8f1c28', v3: '#ba343c', p1: '#e7dbbf', p2: '#fffbf2', u0: '#1d2943', u1: '#2d3f65',
  s1: '#9bb8d3', s2: '#d8e4ea', r1: '#2a2634', r2: '#3c3749', b3: '#eed47c',
};

function px(ctx: CanvasRenderingContext2D, x: number, y: number, c: string, w = 1, h = 1): void {
  ctx.fillStyle = c;
  ctx.fillRect(Math.round(x), Math.round(y), w, h);
}

/** A 1 px line by Bresenham, so hands stay pixel-crisp. */
function line(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, c: string): void {
  x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0);
  const dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  ctx.fillStyle = c;
  for (let i = 0; i < 64; i += 1) {
    ctx.fillRect(x0, y0, 1, 1);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}

/** Clock hands for a wall clock of radius r at local time. */
export function handAngles(date: Date): { hour: number; minute: number; second: number } {
  const s = date.getSeconds();
  const m = date.getMinutes() + s / 60;
  const h = (date.getHours() % 12) + m / 60;
  const a = (k: number) => k * Math.PI * 2 - Math.PI / 2;
  return { hour: a(h / 12), minute: a(m / 60), second: a(s / 60) };
}

function clock(ctx: CanvasRenderingContext2D, e: FxEntry, date: Date, animate: boolean): void {
  const r = e.r ?? 6;
  const { hour, minute, second } = handAngles(date);
  const hl = Math.max(2, Math.round(r * 0.45));
  const ml = Math.max(3, r - 2);
  line(ctx, e.x, e.y, e.x + Math.cos(minute) * ml, e.y + Math.sin(minute) * ml, P.ol);
  line(ctx, e.x, e.y, e.x + Math.cos(hour) * hl, e.y + Math.sin(hour) * hl, P.ol);
  if (animate) line(ctx, e.x, e.y, e.x + Math.cos(second) * (r - 2), e.y + Math.sin(second) * (r - 2), P.v3);
  px(ctx, e.x, e.y, P.b3);
}

function fountain(ctx: CanvasRenderingContext2D, e: FxEntry, now: number, animate: boolean): void {
  const r = e.r ?? 18;
  const cx = e.x; const cy = e.y;
  const t = animate ? now : 0;
  // rings spreading over the water
  for (let k = 0; k < 2; k += 1) {
    const a = ((t / 1900 + k / 2) % 1);
    const rx = 3 + a * (r - 8);
    const ry = rx / 2;
    const n = Math.max(8, Math.round(rx * 2));
    for (let i = 0; i < n; i += 1) {
      if ((i + k) % 3 === 0) continue;
      const ang = (i / n) * Math.PI * 2;
      px(ctx, cx + Math.cos(ang) * rx, cy + Math.sin(ang) * ry, a < 0.75 ? P.s2 : P.s1);
    }
  }
  // the jet and four falling streams
  const top = cy - 13;
  for (let y = top; y < cy - 9; y += 1) px(ctx, cx - 1 + (Math.floor(t / 120 + y) % 2), y, P.p2);
  for (let s = 0; s < 4; s += 1) {
    const dir = s < 2 ? -1 : 1;
    const spread = s % 2 ? 6 : 4;
    for (let d = 0; d < 3; d += 1) {
      const k = (t / 700 + d / 3 + s * 0.17) % 1;
      const x = cx + dir * (1 + k * spread);
      const y = top + 1 - 3 * Math.sin(k * Math.PI) + k * 9;
      px(ctx, x, y, d === 0 ? P.p2 : P.s2);
    }
  }
}

const FLAG_W = 19;
const FLAG_H = 14;

function flag(ctx: CanvasRenderingContext2D, e: FxEntry, now: number, animate: boolean, h: number): void {
  const t = animate ? now / 1000 : 0;
  for (let c = 0; c < FLAG_W; c += 1) {
    const amp = (c / (FLAG_W - 1)) * 1.7;
    const w = Math.sin(c * 0.55 - t * 4.6 - (h % 7));
    const dy = Math.round(w * amp);
    const dark = Math.cos(c * 0.55 - t * 4.6 - (h % 7)) > 0.55;
    for (let r = 0; r < FLAG_H; r += 1) {
      let col: string;
      if (c < 8 && r < 7) col = (r % 2 === 1 && c % 2 === 1) ? (dark ? P.p1 : P.p2) : (dark ? P.u0 : P.u1);
      else col = Math.floor(r / 2) % 2 === 0 ? (dark ? P.v1 : P.v2) : (dark ? P.p1 : P.p2);
      px(ctx, e.x + c, e.y + r + dy, col);
    }
    px(ctx, e.x + c, e.y + FLAG_H + dy, P.ol);
  }
}

function tree(ctx: CanvasRenderingContext2D, e: FxEntry, now: number, animate: boolean, props: HTMLImageElement | null, m: WorldMeta, h: number): void {
  const r = e.r ?? 14;
  const f = m.props.frames[`tree.${r}.${e.v ?? 0}`];
  if (!props || !f) return;
  const cy = e.y - 10 - r;
  const x0 = e.x - r - 1;
  const y0 = cy - r - 1;
  const half = Math.floor(f[3] / 2);
  let dx = 0;
  if (animate) {
    const g = Math.sin(now / 1300 + (h % 13)) * 0.7 + Math.sin(now / 3100 + (h % 5)) * 0.6;
    dx = g > 0.75 ? 1 : g < -0.95 ? -1 : 0;
  }
  ctx.drawImage(props, f[0], f[1] + half, f[2], f[3] - half, x0, y0 + half, f[2], f[3] - half);
  ctx.drawImage(props, f[0], f[1], f[2], half, x0 + dx, y0, f[2], half);
}

function motes(ctx: CanvasRenderingContext2D, e: FxEntry, now: number, animate: boolean, h: number): void {
  if (!animate) return;
  const w = e.w ?? 20; const hh = e.h ?? 20;
  const n = Math.max(3, Math.min(14, Math.floor((w * hh) / 260)));
  for (let i = 0; i < n; i += 1) {
    const s = hashStr(`${h}:${i}`);
    const x0 = s % w;
    const speed = 6000 + (s % 5000);
    const y = hh - (((now + s) % speed) / speed) * hh;
    const x = x0 + Math.sin(now / 2100 + i) * 2 + ((hh - y) * 6) / Math.max(1, hh);
    const tw = (Math.floor((now + s) / 400) % 5) === 0;
    ctx.globalAlpha = tw ? 0.9 : 0.5;
    px(ctx, e.x + ((x % w) + w) % w, e.y + y, '#fff6dc');
  }
  ctx.globalAlpha = 1;
}

function steam(ctx: CanvasRenderingContext2D, e: FxEntry, now: number, animate: boolean, h: number): void {
  if (!animate) return;
  for (let k = 0; k < 3; k += 1) {
    const a = ((now + h + k * 700) % 2100) / 2100;
    ctx.globalAlpha = 0.75 * (1 - a);
    px(ctx, e.x + Math.round(Math.sin(a * 6 + k) * 1.2), e.y - Math.round(a * 8), P.m4);
  }
  ctx.globalAlpha = 1;
}

function drape(ctx: CanvasRenderingContext2D, e: FxEntry, now: number, animate: boolean): void {
  if (!animate) return;
  const w = e.w ?? 10; const hh = e.h ?? 40;
  for (let i = 0; 3 + i * 6 < w; i += 1) {
    const x = e.x + 3 + i * 6 + Math.round(Math.sin(now / 1700 + i * 1.3) * 0.8);
    ctx.fillStyle = 'rgba(255,220,220,0.07)';
    ctx.fillRect(x, e.y, 1, hh);
    ctx.fillStyle = 'rgba(22,15,26,0.10)';
    ctx.fillRect(x + 1, e.y, 1, hh);
  }
}

function pigeons(ctx: CanvasRenderingContext2D, e: FxEntry, now: number, animate: boolean, h: number): void {
  const n = e.n ?? 4; const w = e.w ?? 60; const hh = e.h ?? 30;
  for (let i = 0; i < n; i += 1) {
    const s = hashStr(`${h}:p${i}`);
    const cyc = 11000 + (s % 7000);
    const t = animate ? ((now + s) % cyc) / cyc : 0.3;
    const homeX = s % w; const homeY = (s >>> 8) % hh;
    let x = homeX + (animate ? Math.sin(now / (2600 + (s % 900)) + i) * 4 : 0);
    let y = homeY + (animate ? Math.cos(now / (3100 + (s % 700)) + i) * 2 : 0);
    const fly = t > 0.9;
    if (fly) {
      const k = (t - 0.9) / 0.1;
      x += k * 18 * (s % 2 ? 1 : -1);
      y -= Math.sin(k * Math.PI) * 10;
      const flap = Math.floor(now / 90) % 2;
      const X = e.x + Math.round(x); const Y = e.y + Math.round(y);
      px(ctx, X - 1, Y, P.m1, 3, 1);
      px(ctx, X - 2, Y - flap, P.m2); px(ctx, X + 2, Y - flap, P.m2);
      continue;
    }
    const X = e.x + Math.round(x); const Y = e.y + Math.round(y);
    const peck = animate && Math.floor((now + s) / 330) % 3 === 0;
    const faceR = Math.floor((now + s) / 2300) % 2 === 0;
    px(ctx, X - 1, Y, P.m1, 3, 1);
    px(ctx, X - 1, Y + 1, P.m0, 3, 1);
    px(ctx, faceR ? X + 2 : X - 2, Y + (peck ? 1 : -1), P.r2);
    px(ctx, X, Y + 2, 'rgba(22,15,26,0.25)', 2, 1);
  }
}

function glints(ctx: CanvasRenderingContext2D, e: FxEntry, now: number, animate: boolean, h: number): void {
  if (!animate) return;
  const w = e.w ?? 20; const hh = e.h ?? 10;
  for (let i = 0; i < 4; i += 1) {
    const s = hashStr(`${h}:g${i}`);
    if (Math.floor((now + s) / 500) % 4 !== 0) continue;
    px(ctx, e.x + (s % w), e.y + ((s >>> 6) % hh), P.p2);
  }
}

export type FxLayer = 'ground' | 'tall' | 'air';

/** Which pass an effect is drawn in: on the ground (under people), tall (sorted with people by y), in the air. */
export function fxLayer(kind: string): FxLayer {
  const reg = extra.get(kind);
  if (reg) return reg.layer;
  if (kind === 'tree') return 'tall';
  if (kind === 'fountain' || kind === 'pigeons' || kind === 'water' || kind === 'drape') return 'ground';
  return 'air';
}

export interface FxContext {
  m: WorldMeta;
  now: number;
  date: Date;
  animate: boolean;
  props: HTMLImageElement | null;
}

type FxDraw = (ctx: CanvasRenderingContext2D, e: FxEntry, c: FxContext, h: number) => void;
type FxSortY = (e: FxEntry, c: FxContext) => number;
const extra = new Map<string, { draw: FxDraw; layer: FxLayer; sortY?: FxSortY }>();

/** Add an effect kind from a room's own module `src/world/fx_<rooms>.ts` (the renderer imports every such module), so
 *  rooms never edit this file. A tall effect that moves can give `sortY`, its feet row this frame, for depth sorting. */
export function registerFx(kind: string, draw: FxDraw, layer: FxLayer = 'air', sortY?: FxSortY): void {
  extra.set(kind, { draw, layer, sortY });
}

/** The row a tall effect is sorted by among the people this frame. */
export function fxSortY(e: FxEntry, c: FxContext): number {
  return extra.get(e.kind)?.sortY?.(e, c) ?? e.y;
}

export function drawFx(ctx: CanvasRenderingContext2D, e: FxEntry, c: FxContext): void {
  const h = hashStr(`${e.kind}:${e.x}:${e.y}`);
  const reg = extra.get(e.kind);
  if (reg) { reg.draw(ctx, e, c, h); return; }
  switch (e.kind) {
    case 'clock': clock(ctx, e, c.date, c.animate); break;
    case 'fountain': fountain(ctx, e, c.now, c.animate); break;
    case 'flag': flag(ctx, e, c.now, c.animate, h); break;
    case 'tree': tree(ctx, e, c.now, c.animate, c.props, c.m, h); break;
    case 'light': motes(ctx, e, c.now, c.animate, h); break;
    case 'steam': steam(ctx, e, c.now, c.animate, h); break;
    case 'drape': drape(ctx, e, c.now, c.animate); break;
    case 'pigeons': pigeons(ctx, e, c.now, c.animate, h); break;
    case 'water': glints(ctx, e, c.now, c.animate, h); break;
    default: break;
  }
}

const lampPools = new Map<string, HTMLCanvasElement>();

/** A lamp's pool of light on the floor at art resolution: a squashed disc in three flat stepped bands, the same
 *  light language as the landing's doorlight (flat steps, no gradient and no dither noise). */
function lampPool(r: number, shrink: number): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const key = `${r}:${shrink}`;
  const hit = lampPools.get(key);
  if (hit) return hit;
  const rx = Math.max(6, Math.round(r * 1.15 * (1 - shrink)));
  const ry = Math.max(4, Math.round(rx * 0.62));
  const c = document.createElement('canvas');
  c.width = rx * 2 + 1; c.height = ry * 2 + 1;
  const x = c.getContext('2d');
  if (!x) return null;
  const bands = [0.09, 0.08, 0.08]; // each band adds this much warm light; the three stack to 0.25 at the core
  for (let yy = 0; yy < c.height; yy += 1) {
    for (let xx = 0; xx < c.width; xx += 1) {
      const d = Math.hypot((xx - rx) / rx, (yy - ry) / ry);
      if (d >= 1) continue;
      const lit = Math.ceil((1 - d) * bands.length); // 1 at the rim, 3 at the centre
      let a = 0;
      for (let i = 0; i < Math.min(bands.length, lit); i += 1) a += bands[i];
      if (a <= 0) continue;
      x.fillStyle = `rgba(255,214,140,${a.toFixed(2)})`;
      x.fillRect(xx, yy, 1, 1);
    }
  }
  lampPools.set(key, c);
  return c;
}

/** Lamps at night: a warm, stepped pool on the floor plane that breathes a little (one rim step in or out), drawn
 *  after the night tint so the light is not darkened. */
export function drawLamp(ctx: CanvasRenderingContext2D, e: FxEntry, now: number, animate: boolean): void {
  const r = e.r ?? 16;
  const h = hashStr(`lamp:${e.x}:${e.y}`);
  const flick = animate ? Math.sin(now / 170 + (h % 9)) * Math.sin(now / 530 + (h % 5)) : 0;
  const pool = lampPool(r, flick > 0.45 ? 0.04 : flick < -0.45 ? 0.1 : 0.07);
  if (!pool) return;
  ctx.drawImage(pool, Math.round(e.x - pool.width / 2), Math.round(e.y - pool.height / 2 + 3));
}

let cloudSprite: HTMLCanvasElement | null = null;
/** A soft, dithered cloud shadow at art resolution, made once. */
function cloud(): HTMLCanvasElement | null {
  if (cloudSprite || typeof document === 'undefined') return cloudSprite;
  const W = 150; const H = 70;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const x = c.getContext('2d');
  if (!x) return null;
  const B = [[0, 8, 2, 10], [12, 4, 14, 6], [3, 11, 1, 9], [15, 7, 13, 5]];
  const blobs = [[50, 38, 40, 22], [88, 30, 46, 26], [116, 42, 30, 18], [30, 44, 24, 16]];
  x.fillStyle = 'rgb(22,15,26)';
  for (let yy = 0; yy < H; yy += 1) {
    for (let xx = 0; xx < W; xx += 1) {
      let d = 9;
      for (const [bx, by, rx, ry] of blobs) d = Math.min(d, Math.hypot((xx - bx) / rx, (yy - by) / ry));
      if (d >= 1) continue;
      if (B[yy % 4][xx % 4] / 16 > (1 - d) * 2.2) continue;
      x.fillRect(xx, yy, 1, 1);
    }
  }
  cloudSprite = c;
  return c;
}

/** Cloud shadows drifting west to east over the map (only drawn at map zoom). */
export function drawClouds(ctx: CanvasRenderingContext2D, m: WorldMeta, now: number, animate: boolean): void {
  const c = cloud();
  if (!c) return;
  ctx.globalAlpha = 0.13;
  for (let i = 0; i < 4; i += 1) {
    const k = i % 2 ? 2 : 1;             // whole-number scale only: a 1.5x cloud would be a mixel
    const cw = c.width * k;
    const speed = 5 + i;               // art px per second
    const x = ((animate ? now / 1000 * speed : 0) + i * 610) % (m.map.w + cw) - cw;
    const y = 90 + ((i * 337) % (m.map.h - 260));
    ctx.drawImage(c, Math.round(x), y, cw, c.height * k);
  }
  ctx.globalAlpha = 1;
}
